import { useEffect, useRef } from "react";

/**
 * Fires `callback` exactly once, the first time `condition` becomes true.
 * No-ops on subsequent renders where condition is true.
 *
 * `callback` is stored in a ref so callers may pass inline functions without
 * causing the effect to re-run — the effect triggers only on `condition` changes,
 * and always reads the latest `callback` value via the ref.
 */
export function useOnceWhen(condition: boolean, callback: () => void): void {
  const hasRun = useRef(false);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (condition && !hasRun.current) {
      hasRun.current = true;
      callbackRef.current();
    }
  }, [condition]);
}
